import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import {
  FormDialog,
  OutlinedFieldContainer,
  RhfTextField,
  RhfToggle,
} from '@core/components';
import type { CreateFabricData, Fabric } from '../../api/fabric';
import { createFabric, updateFabric } from '../../api/fabric';
import { useAppContext } from '../context/AppContext';
import type { FabricFlag } from '../utils/fabric';
import { ColorSwatch } from './ColorSwatch';

interface FabricDialogProps {
  open: boolean;
  onClose: () => void;
  fabric?: Fabric | null;
  requiredFlag?: FabricFlag;
  inputValue?: string;
  onSuccess?: (fabric: Fabric) => void;
}

type FabricFormValues = {
  name: string;
  isChasubleBody: boolean;
  isChasublePanel: boolean;
  isAlbBody: boolean;
  isStolePanel: boolean;
  isStolePatching: boolean;
  isMitreBody: boolean;
  isMitrePanel: boolean;
  isMitreLining: boolean;
  colors: string[];
};

const FabricDialog: React.FC<FabricDialogProps> = ({
  open,
  onClose,
  fabric,
  requiredFlag,
  inputValue,
  onSuccess,
}) => {
  const { colors } = useAppContext();
  const isFlagLocked = (flag: FabricFlag) => !fabric && requiredFlag === flag;

  const defaultValues = useMemo<FabricFormValues>(
    () => ({
      name: fabric?.name ?? inputValue?.trim() ?? '',
      isChasubleBody:
        fabric?.isChasubleBody ?? requiredFlag === 'isChasubleBody',
      isChasublePanel:
        fabric?.isChasublePanel ?? requiredFlag === 'isChasublePanel',
      isAlbBody: fabric?.isAlbBody ?? requiredFlag === 'isAlbBody',
      isStolePanel: fabric?.isStolePanel ?? requiredFlag === 'isStolePanel',
      isStolePatching:
        fabric?.isStolePatching ?? requiredFlag === 'isStolePatching',
      isMitreBody: fabric?.isMitreBody ?? requiredFlag === 'isMitreBody',
      isMitrePanel: fabric?.isMitrePanel ?? requiredFlag === 'isMitrePanel',
      isMitreLining: fabric?.isMitreLining ?? requiredFlag === 'isMitreLining',
      colors: fabric?.colors?.map((c) => String(c.id)) ?? [],
    }),
    [fabric, requiredFlag, inputValue]
  );

  const onSubmit = async (data: FabricFormValues) => {
    const payload: CreateFabricData = {
      name: data.name.trim(),
      isChasubleBody: Boolean(data.isChasubleBody),
      isChasublePanel: Boolean(data.isChasublePanel),
      isAlbBody: Boolean(data.isAlbBody),
      isStolePanel: Boolean(data.isStolePanel),
      isStolePatching: Boolean(data.isStolePatching),
      isMitreBody: Boolean(data.isMitreBody),
      isMitrePanel: Boolean(data.isMitrePanel),
      isMitreLining: Boolean(data.isMitreLining),
      colors: data.colors.map(Number),
    };
    if (!fabric && requiredFlag) {
      payload[requiredFlag] = true;
    }
    if (fabric) {
      const updated = await updateFabric(fabric.id, payload);
      toast.success('Fabric updated successfully');
      onSuccess?.(updated);
    } else {
      const created = await createFabric(payload);
      toast.success('Fabric created successfully');
      onSuccess?.(created);
    }
  };

  return (
    <FormDialog<FabricFormValues>
      open={open}
      onClose={onClose}
      title={fabric ? 'Edit Fabric' : 'Add Fabric'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={fabric ? 'Update' : 'Add'}
      cancelText="Cancel"
    >
      {({ control }) => (
        <>
          <RhfTextField<FabricFormValues> name="name" label="Name" required />

          <OutlinedFieldContainer
            label="Can be used for"
            sx={{ display: 'flex', gap: 0.5, px: 1, pt: 0.5, pb: 1 }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <RhfToggle<FabricFormValues>
                name="isChasubleBody"
                label="Chasuble Body"
                disabled={isFlagLocked('isChasubleBody')}
              />
              <RhfToggle<FabricFormValues>
                name="isChasublePanel"
                label="Chasuble Panel"
                disabled={isFlagLocked('isChasublePanel')}
              />
              <RhfToggle<FabricFormValues>
                name="isAlbBody"
                label="Alb Body"
                disabled={isFlagLocked('isAlbBody')}
              />
              <RhfToggle<FabricFormValues>
                name="isStolePanel"
                label="Stole Panel"
                disabled={isFlagLocked('isStolePanel')}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <RhfToggle<FabricFormValues>
                name="isStolePatching"
                label="Stole Patching"
                disabled={isFlagLocked('isStolePatching')}
              />
              <RhfToggle<FabricFormValues>
                name="isMitreBody"
                label="Mitre Body"
                disabled={isFlagLocked('isMitreBody')}
              />
              <RhfToggle<FabricFormValues>
                name="isMitrePanel"
                label="Mitre Panel"
                disabled={isFlagLocked('isMitrePanel')}
              />
              <RhfToggle<FabricFormValues>
                name="isMitreLining"
                label="Mitre Lining"
                disabled={isFlagLocked('isMitreLining')}
              />
            </Box>
          </OutlinedFieldContainer>

          <Controller
            control={control}
            name="colors"
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel id="fabric-colors-label">Colors</InputLabel>
                <Select
                  labelId="fabric-colors-label"
                  multiple
                  value={field.value ?? []}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(
                      typeof value === 'string' ? value.split(',') : value
                    );
                  }}
                  input={<OutlinedInput label="Colors" />}
                  renderValue={(selected) => (
                    <Box display="flex">
                      {selected.map((id) => {
                        const color = colors.find((c) => String(c.id) === id);
                        if (!color) {
                          return null;
                        }
                        return (
                          <ColorSwatch key={color.id} hexCode={color.hexCode} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {colors.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      <ColorSwatch hexCode={c.hexCode}>{c.name}</ColorSwatch>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </>
      )}
    </FormDialog>
  );
};

export default FabricDialog;
